import React from "react";
import {
  ModuleCard, ModuleHeader, SectionDivider, SoftPanel, InfoBanner,
  Grid2, Tag, TableWrap, Th, Td,
} from "../components/ui";

const STEPS = [
  { title: "Step 1", text: "Define the institutional identity, board, academic year, and leadership-level planning context.", tags: ["School Master"] },
  { title: "Step 2", text: "Activate the school's actual grade structure, then map domains and subjects offered.", tags: ["Grades + Subjects"] },
  { title: "Step 3", text: "Build curriculum for beyond academics and vocational areas such as clubs, sports, arts, culture, leadership, and wellbeing.", tags: ["Programs + Clubs"] },
  { title: "Step 4", text: "Delegate planning ownership to the right people, then connect competencies, objectives, assessments, and AI readiness.", tags: [{ label: "Delegated Planning", color: "green" }] },
];

const ROLES = [
  { title: "Leadership Role", text: "School leaders define structure, approve the planning logic, review coverage, and monitor delegation quality." },
  { title: "Execution Role", text: "HODs, teachers, coaches, mentors, and trainers execute detailed planning, competency building, objective definition, and assessment design." },
  { title: "AI Readiness Role", text: "AI readiness tracking, evidence-based planning, and structured class setup allow leadership to see real execution quality, not just planned documents." },
];

const LAYERS = [
  { no: 1, layer: "Foundation Layer", built: "School identity, academic year, board, grades, and top-level school context are established.", owner: "Principal / Leadership", benefit: "Creates the core institutional base for all planning." },
  { no: 2, layer: "Curriculum Backbone", built: "Subjects, domains, clubs, sports, wellbeing, and vocational tracks are mapped.", owner: "Leadership + Academic Coordinators", benefit: "Prevents fragmented planning and repeated setup work." },
  { no: 3, layer: "Delegation Layer", built: "Ownership is assigned to HODs, teachers, mentors, coaches, and trainers.", owner: "Leadership + Department Heads", benefit: "Leaders supervise without doing repetitive low-level planning." },
  { no: 4, layer: "Depth Layer", built: "Competencies, learning objectives, assessments, and time-based evaluation structures are built.", owner: "HODs + Execution Teams", benefit: "Ensures academic depth, not just syllabus listing." },
  { no: 5, layer: "Execution & Intelligence", built: "AI readiness, class structure, and leadership dashboards connect planning to actual execution and evidence.", owner: "Leadership + School Teams", benefit: "Creates a future-ready holistic review system." },
];

const FLOW_TAGS = [
  { label: "1. School Master" },
  { label: "2. Setup Logic" },
  { label: "3. Grades" },
  { label: "4. Academic Planning" },
  { label: "5. Ownership Mapping" },
  { label: "6. Academic Planning Workflow" },
  { label: "7. Assessments", color: "orange" },
  { label: "8. Academic Calendar" },
  { label: "9. Timetable" },
  { label: "10. Student Development", color: "purple" },
  { label: "11. Teacher Development", color: "green" },
];

export default function Module2SetupLogic() {
  return (
    <ModuleCard id="module2">
      <section className="mb-6">
        <div className="text-center px-3 py-2">
          <h3 className="text-4xl font-bold text-[#D67C2C] mb-3">Setup Logic</h3>
          <p className="text-[#1D3469] text-lg font-medium max-w-5xl mx-auto leading-[1.45]">
            One connected execution flow from school setup to ownership, competencies, assessments, and leadership visibility.
          </p>
        </div>
      </section>

      <ModuleHeader
        step="Module 2"
        title="Setup Logic"
        description="This module explains the complete system flow from school setup to curriculum, clubs, competencies, assessments, AI readiness, execution, and leadership visibility."
        statusId="module2StatusText"
        statusDefault="Ready to Review"
        // statusMeta="This module aligns everyone before actual planning begins."
      />

      <InfoBanner>
        This portal is designed for principals and academic leaders first. It does not make school leaders do every detailed planning task themselves. Instead, it builds the academic structure centrally, then delegates the right tasks to HODs, subject teachers, club mentors, sports coaches, wellbeing coordinators, and vocational trainers.
      </InfoBanner>

      <SectionDivider />

      <TableWrap>
        <thead>
          <tr>
            <Th style={{ width: "10%" }}>Step</Th>
            <Th style={{ width: "68%" }}>What Happens</Th>
            <Th style={{ width: "22%" }}>Output</Th>
          </tr>
        </thead>
        <tbody>
          {STEPS.map((s, i) => (
            <tr key={s.title}>
              <Td className="font-semibold text-[#1D3469]">{`0${i + 1}`}</Td>
              <Td className="text-[#374151]">{s.text}</Td>
              <Td>
                <div className="flex flex-wrap gap-2">
                  {s.tags.map(t => typeof t === "string"
                    ? <Tag key={t}>{t}</Tag>
                    : <Tag key={t.label} color={t.color}>{t.label}</Tag>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </TableWrap>

      <SectionDivider />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {ROLES.map(r => (
          <div key={r.title} className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
            <h4 className="m-0 mb-2 text-lg font-semibold text-[#1D3469]">{r.title}</h4>
            <p className="m-0 text-sm text-[#6B7280] leading-[1.8]">{r.text}</p>
          </div>
        ))}
      </div>

      <SectionDivider />

      <TableWrap>
        <thead>
          <tr>
            <Th style={{ width: "5%" }}>#</Th>
            <Th style={{ width: "18%" }}>Layer</Th>
            <Th style={{ width: "35%" }}>What Gets Built</Th>
            <Th style={{ width: "18%" }}>Primary Owner</Th>
            <Th style={{ width: "24%" }}>Leadership Benefit</Th>
          </tr>
        </thead>
        <tbody>
          {LAYERS.map(l => (
            <tr key={l.no}>
              <Td>{l.no}</Td>
              <Td>{l.layer}</Td>
              <Td>{l.built}</Td>
              <Td>{l.owner}</Td>
              <Td>{l.benefit}</Td>
            </tr>
          ))}
        </tbody>
      </TableWrap>

      <SectionDivider />

      <Grid2>
        <SoftPanel title="What makes this different">
          <p className="text-sm text-[#6B7280] leading-[1.8] m-0">
            This is not only a subject planning portal. It connects academics, beyond-academics, vocational skills, assessment structures, and intelligence layers in one yearly execution model.
          </p>
        </SoftPanel>
        <SoftPanel title="Current Portal Flow">
          <div className="flex flex-wrap gap-1.5">
            {FLOW_TAGS.map((t, idx) => (
              <React.Fragment key={t.label}>
                <Tag color={t.color || "default"}>{t.label}</Tag>
                {idx < FLOW_TAGS.length - 1 ? <span className="text-[#9ca3af] text-xs self-center">→</span> : null}
              </React.Fragment>
            ))}
          </div>
        </SoftPanel>
      </Grid2>
    </ModuleCard>
  );
}
